import { memo, type PropsWithChildren, useMemo } from 'react';
import { type DefaultTheme, ThemeProvider } from 'styled-components';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { isInlineLayout } from '../../utils';
import { defaultTheme } from '../constants';
import { useLayout } from '../layout/layout-context';
import { getBorderRadius } from '../utils';

const TIMER_STYLES_WITHOUT_BACKGROUND = ['clean', 'outline'];

const PADDING_SCALE = 1.5;

export const ElementsThemeProvider = memo(({ children }: PropsWithChildren) => {
  const {
    timerStyle,
    timerAnimation,
    timerSeparatorStyle,
    timerShowLabels,
    timerTimeFormat,

    layout,
    theme: {
      themeSettings: {
        headingColor,

        timerColor,
        timerLabelsColor,

        cornerRadius,
        buttonPrimaryColor,
        buttonPrimaryTextColor,
        buttonSecondaryColor,

        ...themeSettings
      }
    }
  } = useSettings();
  const { isMobileBreakpoint, isMediumBreakpoint } = useLayout();

  const { headingFontSize, timerSize, buttonSize } = useMemo(() => {
    const isInline = isInlineLayout(layout);
    const shouldUseMobileStyles = isInline
      ? isMobileBreakpoint
      : isMediumBreakpoint;
    const sizeGroupName = isInline ? 'Inline' : 'Bar';

    const { headingFontSizeKey, timerSizeKey, buttonSizeKey } = (() => {
      if (shouldUseMobileStyles) {
        return {
          headingFontSizeKey: `headingFontSize${sizeGroupName}Mobile`,
          timerSizeKey: `timerSize${sizeGroupName}Mobile`,
          buttonSizeKey: `buttonSize${sizeGroupName}Mobile`
        } as const;
      }

      return {
        headingFontSizeKey: `headingFontSize${sizeGroupName}`,
        timerSizeKey: `timerSize${sizeGroupName}`,
        buttonSizeKey: `buttonSize${sizeGroupName}`
      } as const;
    })();

    return {
      headingFontSize: themeSettings[headingFontSizeKey],
      timerSize: themeSettings[timerSizeKey],
      buttonSize: themeSettings[buttonSizeKey]
    };
  }, [layout, themeSettings, isMobileBreakpoint, isMediumBreakpoint]);

  const heading = useMemo(() => {
    return {
      color: headingColor,
      fontSize: headingFontSize
    };
  }, [headingColor, headingFontSize]);

  const timer = useMemo(() => {
    return {
      timerStyle,
      timerColor: TIMER_STYLES_WITHOUT_BACKGROUND.includes(timerStyle)
        ? timerColor
        : getInvertedColor(timerColor),
      timerBackgroundColor: timerColor,
      separatorColor: timerColor,
      unitLabelColor: timerLabelsColor,
      size: timerSize,
      animation: timerAnimation,
      separatorStyle: timerSeparatorStyle,
      showLabels: timerShowLabels,
      timeFormat: timerTimeFormat
    };
  }, [
    timerStyle,
    timerColor,
    timerLabelsColor,
    timerSize,
    timerAnimation,
    timerSeparatorStyle,
    timerShowLabels,
    timerTimeFormat
  ]);

  const commonButtonStyles = useMemo(() => {
    return {
      font: {
        fontSize: buttonSize,
        fontWeight: 'bold'
      },
      borderRadius: getBorderRadius(cornerRadius, {
        rounded: 8,
        circular: 32
      }),
      horizontalPadding: Math.round(buttonSize * PADDING_SCALE),
      verticalPadding: Math.round(buttonSize / PADDING_SCALE)
    } as const;
  }, [buttonSize, cornerRadius]);
  const primaryButton = useMemo(() => {
    return {
      ...commonButtonStyles,
      fontColor: buttonPrimaryTextColor,
      color: buttonPrimaryColor,
      mode: 'contain' as const
    };
  }, [buttonPrimaryTextColor, buttonPrimaryColor, commonButtonStyles]);
  const secondaryButton = useMemo(() => {
    return {
      ...commonButtonStyles,
      color: buttonSecondaryColor,
      mode: 'outline' as const
    };
  }, [buttonSecondaryColor, commonButtonStyles]);

  const theme: DefaultTheme = useMemo(() => {
    return {
      ...defaultTheme,
      heading,
      timer,
      primaryButton,
      secondaryButton
    };
  }, [heading, timer, primaryButton, secondaryButton]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
});
